import React, { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase-config";
import AudioUpload from "../components/AudioUpload";
import { getUsageLimits } from "../utils/api";

function TranscribePage() {
    const [currentUser, setCurrentUser] = useState(null);
    const [usage, setUsage] = useState({
        transcriptionCount: 0,
        transcriptionMinutes: 0,
        clinicalNoteCount: 0,
        discussCount: 0,
    });
    const [usageLimits, setUsageLimits] = useState(null);
    const [limitsLoading, setLimitsLoading] = useState(false);
    const [limitsError, setLimitsError] = useState("");

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => setCurrentUser(user));
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!currentUser) return;
        const ref = doc(db, "user_profiles", currentUser.uid);
        const unsub = onSnapshot(ref, (snap) => {
            const docData = snap.data() || {};
            const usageCounters = docData.usageCounters || {};
            const getValue = (key) =>
                usageCounters[key] ?? docData[`usageCounters.${key}`] ?? 0;
            setUsage({
                transcriptionCount: getValue("transcriptionCount"),
                transcriptionMinutes: getValue("transcriptionMinutes"),
                clinicalNoteCount: getValue("clinicalNoteCount"),
                discussCount: getValue("discussCount"),
            });
        }, (error) => {
            console.error("usageCounters snapshot error", error);
        });
        return () => unsub();
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser) return;
        const fetchLimits = async () => {
            setLimitsLoading(true);
            setLimitsError("");
            try {
                const result = await getUsageLimits();
                setUsageLimits(result);
            } catch (error) {
                setLimitsError(error.message || "Failed to load limits.");
            } finally {
                setLimitsLoading(false);
            }
        };
        fetchLimits();
    }, [currentUser]);

    const formatUsage = (current = 0, limit) =>
        Number.isFinite(limit) ? { value: current, limit } : { value: current, limit: null };

    const usageItems = [
        { label: "Transcriptions", ...formatUsage(usage.transcriptionCount, usageLimits?.limits?.transcriptions) },
        { label: "Audio Minutes", value: Number(usage.transcriptionMinutes).toFixed(1), limit: null },
        { label: "Clinical Notes", ...formatUsage(usage.clinicalNoteCount, usageLimits?.limits?.clinicalNotes) },
        { label: "Chats", ...formatUsage(usage.discussCount, usageLimits?.limits?.chats) },
    ];

    return (
        <div className="page page-transcribe">
            {/* Page header */}
            <div className="page-header">
                <div className="page-header-eyebrow">Less charting. More dentistry.</div>
                <h1>Tooth <em>Talk</em></h1>
                <p>Record · Transcribe · Generate</p>
            </div>

            {/* Usage strip */}
            {currentUser && (
                <div className="usage-strip">
                    {limitsError && (
                        <span className="status-badge error">⚠ {limitsError}</span>
                    )}
                    {usageItems.map((item, i) => (
                        <React.Fragment key={item.label}>
                            {i > 0 && <div className="usage-divider" />}
                            <div className="usage-item">
                                <div className="usage-label">{item.label}</div>
                                <div className="usage-value">
                                    {item.value}
                                    {item.limit !== null && (
                                        <span> / {item.limit}</span>
                                    )}
                                </div>
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            )}

            <AudioUpload />
        </div>
    );
}

export default TranscribePage;
